import { FC } from "react";
import { Box, Stack, Typography, Divider, Chip, Alert, CircularProgress } from "@mui/material";
import { CheckCircle, Warning, Info } from "@mui/icons-material";

import { ModalV2 } from "@/components";
import { ReconciliationPreviewResponse, ReconciliationFormData } from "@/types/reconciliation";

interface ReconciliationPreviewModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm: () => void;
  previewData?: ReconciliationPreviewResponse;
  formData?: ReconciliationFormData;
  isLoading?: boolean;
  isProcessing?: boolean;
  error?: string;
}

const ReconciliationPreviewModal: FC<ReconciliationPreviewModalProps> = ({
  show,
  setShow,
  onConfirm,
  previewData,
  formData,
  isLoading = false,
  isProcessing = false,
  error,
}) => {
  const formatAdjustmentData = () => {
    if (!formData) return '';
    
    if (formData.adjustmentType === 'value_percentage') {
      const adjustmentData = formData.adjustmentData as { 
        adjustmentMode: 'percentage' | 'amount';
        percentage?: string;
        amount?: number;
        currency?: string;
        rateFactor?: string;
        totalSum?: number;
      };
      
      if (adjustmentData.adjustmentMode === 'percentage') {
        return `Value adjustment of ${adjustmentData.percentage}`;
      } else {
        const rateFactorText = adjustmentData.rateFactor ? ` (${adjustmentData.rateFactor}% rate factor)` : '';
        return `Value adjustment of ${adjustmentData.currency} ${adjustmentData.amount}${rateFactorText}`;
      }
    } else if (formData.adjustmentType === 'combined_value_and_duty') {
      const adjustmentData = formData.adjustmentData as { 
        valueAdjustment: { percentage: string };
        dutyRateAdjustment: { adjustments: Array<{ operation: string }> };
      };
      
      const parts = [`Value adjustment of ${adjustmentData.valueAdjustment.percentage}`];
      
      if (adjustmentData.dutyRateAdjustment?.adjustments) {
        const codeChanges = adjustmentData.dutyRateAdjustment.adjustments.filter(adj => adj.operation === 'code_change').length || 0;
        const rateAdjustments = adjustmentData.dutyRateAdjustment.adjustments.filter(adj => adj.operation === 'rate_adjustment').length || 0;
        
        if (codeChanges > 0) parts.push(`${codeChanges} code change${codeChanges !== 1 ? 's' : ''}`);
        if (rateAdjustments > 0) parts.push(`${rateAdjustments} rate adjustment${rateAdjustments !== 1 ? 's' : ''}`);
      }
      
      return parts.join(' + ');
    } else {
      const adjustmentData = formData.adjustmentData as { adjustments: Array<{ operation: string }> };
      const codeChanges = adjustmentData.adjustments?.filter(adj => adj.operation === 'code_change').length || 0;
      const rateAdjustments = adjustmentData.adjustments?.filter(adj => adj.operation === 'rate_adjustment').length || 0;
      
      const parts = [];
      if (codeChanges > 0) parts.push(`${codeChanges} code change${codeChanges !== 1 ? 's' : ''}`);
      if (rateAdjustments > 0) parts.push(`${rateAdjustments} rate adjustment${rateAdjustments !== 1 ? 's' : ''}`);
      
      return parts.length > 0 ? parts.join(' and ') : 'No adjustments configured';
    }
  };

  const formatFilters = () => {
    if (!formData) return [];
    
    const filters = [];
    
    if (formData.filters.reconFlag) {
      filters.push(`Recon Flag: ${formData.filters.reconFlag === 'Y' ? 'Reconciled' : 'Non-reconciled'}`);
    }
    
    if (formData.filters.htsCodes && formData.filters.htsCodes.length > 0) {
      filters.push(`HTS Codes: ${formData.filters.htsCodes.length} selected`);
    }
    
    if (formData.filters.originCodes && formData.filters.originCodes.length > 0) {
      filters.push(`Countries: ${formData.filters.originCodes.length} selected`);
    }
    
    if (formData.filters.manufacturerIds && formData.filters.manufacturerIds.length > 0) {
      filters.push(`Manufacturers: ${formData.filters.manufacturerIds.length} selected`);
    }
    
    if (formData.filters.entryLines && formData.filters.entryLines.length > 0) {
      filters.push(`Entry Lines: ${formData.filters.entryLines.length} selected`);
    }

    return filters;
  };

  const getSeverityIcon = (count: number) => {
    if (count === 0) return <Warning color="warning" />;
    if (count > 10000) return <Warning color="error" />;
    return <CheckCircle color="success" />;
  };

  const getSeverityColor = (count: number) => {
    if (count === 0) return "warning";
    if (count > 10000) return "error";
    return "success";
  };

  return (
    <ModalV2
      show={show}
      setShow={(show) => setShow(show)}
      buttonAction={onConfirm}
      isConfirmButtonLoading={isProcessing}
      buttonText="Process Adjustment"
      title="Adjustment Preview"
      subTitle="Review the scope and impact before processing"
      maxWidth="md"
    >
      <Stack spacing={3}>
        {/* Loading State */}
        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body2" color="textSecondary">
                Analyzing adjustment scope...
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" icon={<Warning />}>
            <Typography variant="body2">
              <strong>Preview Failed:</strong> {error}
            </Typography>
          </Alert>
        )}

        {/* Preview Data */}
        {previewData && !isLoading && (
          <>
            {/* Summary Card */}
            <Box 
              sx={{ 
                p: 3, 
                border: '1px solid',
                borderColor: getSeverityColor(previewData.recordCount) === 'error' ? 'error.main' : 
                           getSeverityColor(previewData.recordCount) === 'warning' ? 'warning.main' : 'success.main',
                borderRadius: 2,
                bgcolor: getSeverityColor(previewData.recordCount) === 'error' ? 'rgba(211, 47, 47, 0.08)' : 
                        getSeverityColor(previewData.recordCount) === 'warning' ? 'rgba(237, 108, 2, 0.08)' : 'rgba(46, 125, 50, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              {getSeverityIcon(previewData.recordCount)}
              <Stack flex={1}>
                <Typography variant="h6" component="div">
                  {previewData.recordCount.toLocaleString()} Records Found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {previewData.recordCount === 0 && "No records match the specified criteria"}
                  {previewData.recordCount > 0 && previewData.recordCount <= 10000 && "Ready for processing"}
                  {previewData.recordCount > 10000 && "Large dataset - consider narrowing filters for optimal performance"}
                </Typography>
              </Stack>
              <Chip 
                label={`${previewData.estimatedProcessingTime}`}
                color={getSeverityColor(previewData.recordCount) as any}
                variant="outlined"
                size="small"
              />
            </Box>

            {/* Configuration Summary */}
            <Box>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <Info color="primary" />
                Configuration Summary
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Date Range
                  </Typography>
                  <Typography variant="body2">
                    {formData?.dateRange.start} to {formData?.dateRange.end}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adjustment
                  </Typography>
                  <Typography variant="body2">
                    {formatAdjustmentData()}
                  </Typography>
                </Box>

                {formatFilters().length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Active Filters
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {formatFilters().map((filter, index) => (
                        <Chip 
                          key={index}
                          label={filter}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Impact Summary */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Expected Changes
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Adjustment Summary
                  </Typography>
                  <Typography variant="body2">
                    {previewData.adjustmentSummary}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Fields to be Modified
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {previewData.affectedFields.map((field, index) => (
                      <Chip 
                        key={index}
                        label={field}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Box>

            {/* Warnings */}
            {previewData.recordCount > 5000 && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Large Dataset Notice:</strong> Processing {previewData.recordCount.toLocaleString()} records may take several minutes. 
                  You will be able to download the results when processing is complete.
                </Typography>
              </Alert>
            )}

            {previewData.recordCount === 0 && (
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>No Records Found:</strong> The current filters don't match any records. 
                  Consider adjusting your date range or filters and try again.
                </Typography>
              </Alert>
            )}
          </>
        )}

        {/* Detailed Adjustment Info for Duty Adjustments */}
        {formData?.adjustmentType === 'duty_rate_adjustment' && previewData && !isLoading && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Duty Adjustments
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {((formData?.adjustmentData as { adjustments: Array<any> })?.adjustments || [])
                .map((adjustment, index) => (
                  <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                    {adjustment.operation === 'code_change' ? (
                      <Stack>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: 'primary.dark' }}>
                          Code Change
                        </Typography>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" fontFamily="monospace" sx={{ color: 'text.primary' }}>
                            {adjustment.originalCode} → {adjustment.targetCode}
                          </Typography>
                        </Stack>
                        {adjustment.subEntries && adjustment.subEntries.length > 0 && (
                          <Box sx={{ ml: 2, mt: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">
                              CH99 Sub-entries:
                            </Typography>
                            {adjustment.subEntries.map((sub: any, subIdx: number) => (
                              <Typography key={subIdx} variant="caption" display="block" fontFamily="monospace" sx={{ ml: 1, color: 'text.secondary' }}>
                                {sub.ch99Field || 'ch99Code'}: {sub.ch99Original} → {sub.ch99Target}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <Stack>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: 'secondary.dark' }}>
                          Rate Adjustment
                        </Typography>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" fontFamily="monospace" sx={{ color: 'text.primary' }}>
                            {adjustment.htsCode}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            New Rate: {((adjustment.newRate || 0) * 100).toFixed(2)}%
                          </Typography>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                ))}
            </Box>
          </Box>
        )}

        {/* Detailed Adjustment Info for Combined Adjustments */}
        {formData?.adjustmentType === 'combined_value_and_duty' && previewData && !isLoading && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Combined Value and Duty Adjustments
            </Typography>
            
            {/* Value Adjustment Section */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'primary.dark' }}>
                Step 1: Value Adjustment
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                Increase all values by {(formData?.adjustmentData as any)?.valueAdjustment?.percentage}
              </Typography>
            </Box>
            
            {/* Duty Adjustments Section */}
            {((formData?.adjustmentData as any)?.dutyRateAdjustment?.adjustments || []).length > 0 && (
              <Box sx={{ p: 2, bgcolor: 'rgba(156, 39, 176, 0.08)', borderRadius: 1, border: '1px solid', borderColor: 'secondary.main' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ color: 'secondary.dark' }}>
                  Step 2: Duty Rate Changes (Applied to Increased Values)
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {((formData?.adjustmentData as any)?.dutyRateAdjustment?.adjustments || [])
                    .map((adjustment: any, index: number) => (
                      <Box key={index} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                        {adjustment.operation === 'code_change' ? (
                          <Stack>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'primary.dark' }}>
                              Code Change
                            </Typography>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" fontFamily="monospace" sx={{ color: 'text.primary' }}>
                                {adjustment.originalCode} → {adjustment.targetCode}
                              </Typography>
                            </Stack>
                            {adjustment.subEntries && adjustment.subEntries.length > 0 && (
                              <Box sx={{ ml: 2, mt: 0.5 }}>
                                <Typography variant="caption" color="textSecondary">
                                  CH99 Sub-entries:
                                </Typography>
                                {adjustment.subEntries.map((sub: any, subIdx: number) => (
                                  <Typography key={subIdx} variant="caption" display="block" fontFamily="monospace" sx={{ ml: 1, color: 'text.secondary' }}>
                                    {sub.ch99Field || 'ch99Code'}: {sub.ch99Original} → {sub.ch99Target || sub.ch99Original}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Stack>
                        ) : (
                          <Stack>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'secondary.dark' }}>
                              Rate Adjustment
                            </Typography>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" fontFamily="monospace" sx={{ color: 'text.primary' }}>
                                {adjustment.htsCode}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                New Rate: {((adjustment.newRate || 0) * 100).toFixed(2)}%
                              </Typography>
                            </Stack>
                          </Stack>
                        )}
                      </Box>
                    ))}
                </Box>
              </Box>
            )}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                The duty rates will be applied to the increased values, resulting in a combined impact on the total duties owed.
              </Typography>
            </Alert>
          </Box>
        )}
      </Stack>
    </ModalV2>
  );
};

export default ReconciliationPreviewModal;